import { DestroyRef, Injectable, WritableSignal, inject, signal } from '@angular/core'
import { MockService } from './mock.service'
import { AdminService } from './admin.service'
import { LogService } from './log.service'
import { EventService } from './event.service'
import { debounceTime } from 'rxjs'
import { merge } from 'lodash-es'
import { FetchService } from './fetch.service'
import { ApiRequestService } from './api-request.service'
import { HttpErrorResponse } from '@angular/common/http'
import { ClientService } from './client.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AuthService } from '@auth0/auth0-angular'
import { EnvironmentService } from './environment.service'
import { mockConfigMetadata } from '../mock-data/config.mock-data'
import { currentConfigVersion } from '../versions/processors'
import { CurrencyCode } from '../interfaces/currency.interface'
import { KeysService } from './keys.service'
import { RoutePaths } from 'src/app/core/data/route-paths.data'
import { LanguageCode } from '../interfaces/language.interface'
import { defaultDetailsPageSiteTitle, defaultHomePageSiteTitle, defaultSearchPageSiteTitle } from '../i18n/config-defaults'
import { IAdminConfigData, IApiDeleteResponse, IApiNewResponse, IApiSaveRequest, IApiSaveResponse, IConfig, IConfigMetadata, IConfigValidation, IConfigValidationResult } from '../interfaces/config.interface'
import { ImageSizes } from '../interfaces/media-library.interface'
import { StorageService } from './storage.service'
import { GlobalDataService } from './global-data.service'
import { defaultCustomFilterItems, defaultFilterItems, defaultQuickFilterItems } from '../data/quick-filters.data'
import { getDefaultColorSchemeMapping, getDefaultColorSchemes } from '../data/color-schemes.data'
import { defaultLayoutOrder } from '../data/home-page.data'
import { getBrowseByCategoryDefaultConfig, getBrowseByMakeDefaultConfig, getContactFormSectionDefaultConfig, getFeaturedCarouselDefaultConfig, getHeroBannerDefaultConfig } from '../data/config-defaults.data'

declare global {
  interface Window { configService: ConfigService; }
}

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  protected mockService = inject(MockService)
  protected adminService = inject(AdminService)
  protected logger = inject(LogService)
  protected eventService = inject(EventService)
  protected fetchService = inject(FetchService)
  protected apiRequestService = inject(ApiRequestService)
  protected clientService = inject(ClientService)
  protected auth = inject(AuthService)
  protected envService = inject(EnvironmentService)
  protected keysService = inject(KeysService)
  protected storageService = inject(StorageService)

  private destroyRef = inject(DestroyRef)
  private _state = inject(GlobalDataService)

  private _configMetadata!: WritableSignal<IConfigMetadata>
  private _loadConfigIdKey = 'load-config-id'
  private _localConfigKey = 'config-in-progress'
  private _localConfigMetadataKey = 'config-metadata'
  private _localDirtyKey = 'config-dirty-state'
  initialized: boolean = false

  validations: IConfigValidation[] = []

  constructor() {
    this._state.config.set(this.create())
    window.configService = this
  }

  private _adminConfigs: IAdminConfigData[] = []

  get adminConfigs(): IAdminConfigData[] {
    return this._adminConfigs
  }

  loadAdminConfigs(configs: IAdminConfigData[]) {
    this._adminConfigs = configs
  }

  sortAdminConfigs(configs: IAdminConfigData[]) {
    return configs.sort((a, b) => {
      // Published config always comes first
      if (a.is_live) {
        return -1
      }
      if (b.is_live) {
        return 1
      }

      // Currently loaded config comes second
      if (a.id === this._configMetadata().id) {
        return -1
      }
      if (b.id === this._configMetadata().id) {
        return 1
      }

      // Everything else sort by date_modified in descending order
      return new Date(b.date_modified).getTime() - new Date(a.date_modified).getTime()
    })
  }

  initForAdmin(): void {
    this.eventService.configUpdated$.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(200),
    ).subscribe(() => {
      this.saveLocally()
      this.markAsDirty()
    })
    this._adminConfigs = this.sortAdminConfigs(this._adminConfigs)
  }

  async save(): Promise<void> {
    this.logger.debug('Saving config: ', this._state.config())
    if (this.envService.get().useMock) {
      this.logger.debug('Mock enabled, not actually saving.')
      this.clearDirty()
      return Promise.resolve()
    }
    const requestOptions = await this.apiRequestService.buildRequestOptions({ headers: { authorized: true } })
    return new Promise((resolve, reject) => {
      this.fetchService.putRequest<IApiSaveResponse>(`${this.apiRequestService.buildEndpoint('admin/configuration')}`, {
        config: this._state.config(),
        client_id: this.clientService.client().id,
        catalog_id: this._configMetadata().id,
        name: this._configMetadata().name,
        is_live: this._configMetadata().is_live,
      } as IApiSaveRequest, requestOptions).pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(response => {
        if (response instanceof HttpErrorResponse) {
          this.logger.error('Unable to save config.  ', response)
          return reject('Backend returned an error.')
        }
        this.logger.info(`Config [${this._configMetadata().name}] saved.`)
        const fullConfigData = this._adminConfigs.find(c => c.id === this._configMetadata().id)
        if (fullConfigData) {
          fullConfigData.config = JSON.parse(JSON.stringify(this._state.config()))
          fullConfigData.date_modified = new Date().toString()
        }
        this.clearDirty()
        resolve()
      })
    })
  }

  saveLocally(config?: IConfig, configMetadata?: IConfigMetadata): void {
    this.logger.debug('Saving config locally: ', config ?? this._state.config())
    this.logger.debug('Saving config metadata locally: ', configMetadata ?? this._configMetadata())
    this.storageService.setItem(this._localConfigKey, JSON.stringify(config ?? this._state.config()))
    this.storageService.setItem(this._localConfigMetadataKey, JSON.stringify(configMetadata ?? this._configMetadata()))
    this.eventService.notifyConfigSavedLocally()
  }

  clearLocal(): void {
    this.storageService.removeItem(this._localConfigKey)
    this.storageService.removeItem(this._localConfigMetadataKey)
  }

  checkLocalExists(): boolean {
    return !!this.storageService.getItem(this._localConfigKey)
  }

  checkLocalExistsInData(): boolean {
    return !!this._adminConfigs.find(c => c.id === this.configMetadata().id)
  }

  markAsDirty(): void {
    this.storageService.setItem(this._localDirtyKey, 'true')
    this.eventService.notifyConfigDirtyChanged(true)
  }

  clearDirty(): void {
    this.storageService.removeItem(this._localDirtyKey)
    this.eventService.notifyConfigDirtyChanged(false)
  }

  checkIsDirty(): boolean {
    return this.storageService.getItem(this._localDirtyKey) === 'true'
  }

  reset(): void {
    this.logger.debug('Clearing local config.')
    this.loadConfigId = this.configMetadata().id
    this.clearLocal()
    this.clearDirty()
    window.location.reload()
  }

  async publish(): Promise<void> {
    this.logger.debug('Publishing config: ', this._state.config())
    if (this.envService.get().useMock) {
      this.logger.debug('Mock enabled, not actually publishing.')
      this.clearDirty()
      return Promise.resolve()
    }
    const requestOptions = await this.apiRequestService.buildRequestOptions({ headers: { authorized: true } })
    return new Promise((resolve, reject) => {
      this.fetchService.putRequest<IApiSaveResponse>(`${this.apiRequestService.buildEndpoint('admin/configuration')}`, {
        config: this._state.config(),
        client_id: this.clientService.client().id,
        catalog_id: this._configMetadata().id,
        name: this._configMetadata().name,
        is_live: true,
      }, requestOptions).pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(response => {
        if (response instanceof HttpErrorResponse) {
          this.logger.error('Unable to publish config.  ', response)
          return reject('Backend returned an error.')
        }
        this.logger.info(`Config [${this._configMetadata().name}] published.`)
        const fullConfigData = this._adminConfigs.find(c => c.id === this._configMetadata().id)
        if (fullConfigData) {
          fullConfigData.is_live = true
          this._adminConfigs.filter(c => c.id !== this._configMetadata().id).forEach(c => c.is_live = false)
        }
        this.saveLocally(this._state.config(), {
          ...this._configMetadata(),
          is_live: true,
        })
        this._configMetadata().is_live = true
        this.clearDirty()
        resolve()
      })
    })
  }

  async new(name: string): Promise<void> {
    this.logger.debug('Creating a new config.')

    if (this.envService.get().useMock) {
      this.logger.debug('Mock enabled, not actually creating new.')
      this.saveLocally(this.create(), {
        ...mockConfigMetadata,
        name,
      })
      this.clearDirty()
      return Promise.resolve()
    }
    if (name.trim() === '') {
      return Promise.reject('Name required')
    }
    // save new config and get config id
    const requestOptions = await this.apiRequestService.buildRequestOptions({ headers: { authorized: true } })
    return new Promise((resolve, reject) => {
      this.fetchService.putRequest<IApiNewResponse>(`${this.apiRequestService.buildEndpoint('admin/configuration')}`, {
        config: this.create(),
        name,
        client_id: this.clientService.client().id,
      } as IApiSaveRequest, requestOptions).pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(response => {
        if (response instanceof HttpErrorResponse) {
          this.logger.error('Unable to create config.  ', response)
          return reject(response.error)
        }
        if (!response.success) {
          this.logger.error('Creating config was not successful.  ', response.reason)
          return reject(response.reason)
        }
        this.logger.info(`Config [${name}] created.`)
        this.loadConfigId = response.catalog_id
        this.clearLocal()
        this.clearDirty()
        resolve()
      })
    })
  }

  async copy(configId: number, name: string): Promise<void> {
    this.logger.debug('Copying config at id [', configId ,']')
    const configToCopy = this._adminConfigs.find(c => c.id === configId)
    if (!configToCopy) {
      this.logger.error('Could not find config to copy at id: ', configId)
      return Promise.reject()
    }
    const configCopy = Object.assign({}, configToCopy, {
      name,
      is_live: false,
    })
    if (this.envService.get().useMock) {
      this.logger.debug('Mock enabled, not actually copying config.')
      const newConfig = {
        ...configCopy,
        catalog_id: Math.round(Math.random() * 100000),
      }
      this._adminConfigs.push(newConfig)
      return Promise.resolve()
    }
    const requestOptions = await this.apiRequestService.buildRequestOptions({ headers: { authorized: true } })
    return new Promise((resolve, reject) => {
      this.fetchService.putRequest<IApiNewResponse>(`${this.apiRequestService.buildEndpoint('admin/configuration')}`, {
        config: configCopy.config,
        name,
        client_id: this.clientService.client().id,
      }, requestOptions).pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(response => {
        if (response instanceof HttpErrorResponse) {
          this.logger.error('Unable to copy config.  ', response)
          return reject(response.error)
        }
        if (!response.success) {
          this.logger.error('Copying config was not successful.  ', response.reason)
          return reject(response.reason)
        }
        this.logger.info(`Config [${name}] created.`)
        configCopy.id = response.catalog_id
        this._adminConfigs.push(configCopy)
        resolve()
      })
    })
  }

  async rename(configId: number, name: string): Promise<void> {
    this.logger.debug('Renaming config at id [', configId ,']')
    const configToRename = this._adminConfigs.find(c => c.id === configId)
    if (!configToRename) {
      this.logger.error('Could not find config to rename at id: ', configId)
      return Promise.reject()
    }
    if (this.envService.get().useMock) {
      this.logger.debug('Mock enabled, not actually renaming config.')
      configToRename.name = name
      return Promise.resolve()
    }
    const requestOptions = await this.apiRequestService.buildRequestOptions({ headers: { authorized: true } })
    return new Promise((resolve, reject) => {
      this.fetchService.putRequest<IApiSaveResponse>(`${this.apiRequestService.buildEndpoint('admin/configuration')}`, {
        config: { ...configToRename.config },
        client_id: this.clientService.client().id,
        catalog_id: configToRename.id,
        name,
      }, requestOptions).pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(response => {
        if (response instanceof HttpErrorResponse) {
          this.logger.error('Unable to rename config.  ', response)
          return reject(response.error)
        }
        if (!response.success) {
          this.logger.error('Renaming config was not successful.  ', response.reason)
          return reject(response.reason)
        }
        this.logger.info(`Config [${name}] renamed.`)
        configToRename.name = name
        if (this.configMetadata().id === configToRename.id) {
          this.configMetadata().name = name
          this.saveLocally(configToRename.config, this.configMetadata())
        }
        resolve()
      })
    })
  }


  async delete(configId: number): Promise<void> {
    this.logger.debug('Deleting config at id [', configId ,']')
    const configToDelete = this._adminConfigs.find(c => c.id === configId)
    if (!configToDelete) {
      this.logger.error('Could not find config to delete at id: ', configId)
      return Promise.reject()
    }
    if (this.envService.get().useMock) {
      this.logger.debug('Mock enabled, not actually deleting config.')
      this._adminConfigs = this._adminConfigs.filter(c => c.id !== configId)
      return Promise.resolve()
    }
    const requestOptions = await this.apiRequestService.buildRequestOptions({ headers: { authorized: true } })
    return new Promise((resolve, reject) => {
      this.fetchService.deleteRequest<IApiDeleteResponse>(
        `${this.apiRequestService.buildEndpoint('admin/configuration')}/${configId}`,
        requestOptions
      ).pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(response => {
        if (response instanceof HttpErrorResponse) {
          this.logger.error('Unable to delete config.  ', response)
          return reject(response.error)
        }
        if (!response.success) {
          this.logger.error('Deleting config was not successful.  ', response.reason)
          return reject(response.reason)
        }
        this.logger.info(`Config [${configToDelete.name}] deleted.`)
        this._adminConfigs = this._adminConfigs.filter(c => c.id !== configId)
        resolve()
      })
    })
  }

  loadConfig(config: IConfig): void {
    this._state.config.set(merge(
      this.create(),
      config,
    ))
  }

  loadConfigForAdmin(): Promise<void> {
    if (this._adminConfigs.length === 0) {
      this.logger.error(`There are no configs for this client!`)
      return Promise.reject()
    }

    if (this.loadConfigId || !this.storageService.getItem(this._localConfigKey)) {
      let fullConfig: IAdminConfigData | undefined
      if (this.loadConfigId) {
        this.logger.debug(`Trying to load config at catalog_id=[${this.loadConfigId}]`)
        fullConfig = this._adminConfigs.find(c => c.id === this.loadConfigId)
        this.loadConfigId = null
      }
      if (!fullConfig) {
        this.logger.debug(`Trying to load live config`)
        fullConfig = this._adminConfigs.find(c => c.is_live)
        if (!fullConfig) {
          this.logger.debug(`Trying to load first available config`)
          fullConfig = this._adminConfigs[0]
        }
      }
      this.saveLocally(fullConfig.config, {
        id: fullConfig.id,
        is_live: fullConfig.is_live,
        name: fullConfig.name,
        date_modified: fullConfig.date_modified,
      })
    }
    this.loadFromLocal()
    this.initialized = true
    return Promise.resolve()
  }

  loadFromLocal(): void {
    this._state.config.set(merge(
      this.create(),
      this.parseLocal(),
    ))
    this._configMetadata = signal(this.parseLocalMetadata())
  }

  parseLocal(): any {
    let localConfig
    try {
      localConfig = this.storageService.getItem(this._localConfigKey)
      if (localConfig) {
        return JSON.parse(localConfig)
      }
    } catch (e) {
      this.logger.error('Error parsing local config: ', localConfig)
    }
    return {}
  }

  parseLocalMetadata(): any {
    let localConfigMetadata
    try {
      localConfigMetadata = this.storageService.getItem(this._localConfigMetadataKey)
      if (localConfigMetadata) {
        return JSON.parse(localConfigMetadata)
      }
    } catch (e) {
      this.logger.error('Error parsing local config metadata: ', localConfigMetadata)
    }
    return {}
  }

  get loadConfigId(): number | null {
    const configIdStr = this.storageService.getItem(this._loadConfigIdKey)
    return configIdStr ? parseInt(configIdStr) : null
  }

  set loadConfigId(configId: number | null) {
    if (!configId) {
      this.storageService.removeItem(this._loadConfigIdKey)
      return
    }
    this.storageService.setItem(this._loadConfigIdKey, configId.toString())
  }

  get config(): WritableSignal<IConfig> {
    return this._state.config
  }

  get configMetadata(): WritableSignal<IConfigMetadata> {
    return this._configMetadata
  }

  get allConfigs(): IAdminConfigData[] {
    return this._adminConfigs
  }

  addValidation(validation: IConfigValidation): void {
    this.validations.push(validation)
  }

  checkValidations(): IConfigValidationResult[] {
    const results = this.validations.map(validation => validation())
    return results.filter(result => !result.valid)
  }

  create(): IConfig {
    return {
      version: currentConfigVersion,
      sitemap: {
        static: [
          '/home',
          `${RoutePaths.Search}`
        ],
        dynamic: [
          {
            url: `${RoutePaths.Details}`,
            id: 'equipment.equipment_number',
            filters: [
              {
                'equipment_marketing.is_listed': true
              }
            ]
          }
        ]
      },
      seo: {
        data: {
          favicon_img_data: {
            width: 0,
            height: 0,
            kb: 0,
            name: '',
            originalFileName: '',
            updated: '',
            updatedFormatted: '',
            urls: {
              [ImageSizes.Favicon]: '',
            },
          },
          social_img_data: {
            height: 0,
            width: 0,
            kb: 0,
            name: '',
            originalFileName: '',
            updated: '',
            updatedFormatted: '',
            urls: {
              [ImageSizes.SocialMediaShare]: '',
            }
          },
        },
      },
      general: {
        data: {
          default_img_data: {
            height: 0,
            width: 0,
            kb: 0,
            name: '',
            originalFileName: '',
            updated: '',
            updatedFormatted: '',
            urls: {
              [ImageSizes.DefaultProductImage]: '',
            }
          },
          watermark_logo: false,
          // TODO: Move to contactForm.data in future for better organization
          contact_form_acknowledgement_enabled: false,
        },
        saveAutofillData: false,
      },
      currency: {
        available: [CurrencyCode.USD],
        default: CurrencyCode.USD,
        enabled: true,
        template: 'basic',
        data: {
          styles: {},
          components: {},
        }
      },
      language: {
        options: {
          base: {
            available: [LanguageCode.EN_US],
            default: LanguageCode.EN_US,
          }
        },
      },
      theme: {
        id: 'light-theme',
        fonts: {},
        overrides: {},
        settings: {
          ...getDefaultColorSchemeMapping(),
        },
        schemes: [
          ...getDefaultColorSchemes(),
        ],
      },
      financing: {},
      header: {
        enabled: true,
        template: 'basic',
        data: {
          logo: {
            src_data: {
              name: '',
            },
          },
          headerTopper: {
            display: false,
            phone: '',
          },
          menu: [],
          styles: {},
          components: {},
        }
      },
      footer: {
        enabled: true,
        template: 'basic',
        data: {
          styles: {},
          components: {},
        }
      },
      assetCard: {
        enabled: true,
        template: 'basic',
        data: {
          styles: {},
          components: {},
        }
      },
      legalTerms: {
        enabled: true,
        template: 'basic',
        data: {
          terms: '',
          styles: {},
          components: {},
        }
      },
      comparison: {
        enabled: true,
        template: 'basic',
        data: {
          styles: {},
          components: {},
        }
      },
      contactForm: {
        enabled: true,
        template: 'basic',
        data: {
          styles: {},
          components: {
            assetCard: {
              enabled: true,
              template: 'contact-form',
              data: {
                styles: {},
                components: {
                  badgesContainer: {
                    enabled: false,
                    template: 'basic',
                    data: {
                      styles: {},
                      components: {},
                    }
                  }
                },
              }
            }
          },
        }
      },
      page: {
        home: {
          seo: {
            data: {
              title: {
                ...defaultHomePageSiteTitle,
              },
              description: '',
            },
          },
          layout: {
            order: [
              ...defaultLayoutOrder,
            ],
          },
          components: {
            heroBanner: getHeroBannerDefaultConfig(),
            featuredCarousel: getFeaturedCarouselDefaultConfig(),
            browseByCategory: getBrowseByCategoryDefaultConfig(),
            browseByMake: getBrowseByMakeDefaultConfig(),
            contactFormSection: getContactFormSectionDefaultConfig(),
          },
        },
        search: {
          seo: {
            data: {
              title: {
                ...defaultSearchPageSiteTitle,
              },
              description: '',
            },
          },
          components: {
            search: {
              enabled: true,
              template: 'basic',
              data: {
                styles: {},
                components: {},
              }
            },
            searchGrid: {
              enabled: true,
              template: 'basic',
              data: {
                styles: {},
                components: {
                  assetCard: {
                    enabled: true,
                    template: 'basic',
                    data: {
                      enableButtons: false,
                      styles: {},
                      components: {
                        badgesContainer: {
                          enabled: true,
                          template: 'basic',
                          data: {
                            styles: {},
                            components: {
                              badge: {
                                enabled: true,
                                template: 'basic',
                                data: {
                                  styles: {},
                                  components: {},
                                }
                              }
                            },
                          }
                        }
                      },
                    }
                  }
                },
              }
            },
            filterContainer: {
              enabled: true,
              template: 'basic',
              data: {
                styles: {},
                items: [
                  ...defaultFilterItems,
                  ...defaultCustomFilterItems,
                ],
                customFilterLabels: null,
                components: {
                  quickFilters: {
                    enabled: true,
                    template: 'basic',
                    data: {
                      items: [
                        ...defaultQuickFilterItems,
                      ],
                      styles: {},
                      components: {},
                    }
                  }
                },
              }
            },
          },
        },
        details: {
          seo: {
            data: {
              title: {
                ...defaultDetailsPageSiteTitle,
              },
              description: '',
            }
          },
          components: {
            shareButton: {
              enabled: true,
              template: 'basic',
              data: {
                styles: {},
                components: {},
              }
            },
            leadForm: {
              enabled: true,
              template: 'basic',
              data: {
                styles: {},
                components: {},
              }
            },
            quickLinksBanner: {
              enabled: true,
              template: 'basic',
              data: {
                styles: {},
                components: {},
              }
            },
            similarCarousel: {
              enabled: false,
              template: 'basic',
              data: {
                styles: {},
                components: {
                  assetCard: {
                    enabled: true,
                    template: 'basic',
                    data: {
                      styles: {},
                      components: {
                        badgesContainer: {
                          enabled: true,
                          template: 'basic',
                          data: {
                            styles: {},
                            components: {
                              badge: {
                                enabled: true,
                                template: 'basic',
                                data: {
                                  styles: {},
                                  components: {},
                                }
                              }
                            },
                          }
                        }
                      },
                    }
                  }
                },
              }
            },
            recentlyViewedCarousel: {
              enabled: false,
              template: 'basic',
              data: {
                styles: {},
                components: {
                  assetCard: {
                    enabled: true,
                    template: 'basic',
                    data: {
                      styles: {},
                      components: {
                        badgesContainer: {
                          enabled: true,
                          template: 'basic',
                          data: {
                            styles: {},
                            components: {
                              badge: {
                                enabled: true,
                                template: 'basic',
                                data: {
                                  styles: {},
                                  components: {},
                                }
                              }
                            },
                          }
                        }
                      },
                    }
                  }
                },
              }
            },
            assetDetailsHeader: {
              enabled: true,
              template: 'basic',
              data: {
                styles: {},
                components: {},
              }
            },
            assetInformation: {
              enabled: true,
              template: 'basic',
              data: {
                showConfiguration: true,
                styles: {},
                components: {},
              }
            },
            shippingBanner: {
              enabled: true,
              template: 'basic',
              data: {
                styles: {},
                components: {},
              }
            },
          },
        },
      }
    }
  }
}
